// The device manager — lists the Owner's cameras (name, status, last Sync) with a
// revoke action. `GET /api/devices` → rows; `POST /api/devices/:id/revoke` flips a
// row to `revoked` (its Device token then fails the camera's next /device/*
// request — instant revocation, ADR-0021). Revoking is confirmed inline (a sold/
// lost unit losing access is destructive), and the row reflects the new status
// immediately from the Worker's response.
//
// Presentational over the `useDevices` hook (data/sequencing lives there). The
// SignedOut affordance handles a 401/403 here just like the gallery.

import { useState } from "react";
import type { ApiClient, Device, DeviceStatus } from "../api";
import { useDevices } from "../useDevices";
import { formatRelative, formatAbsolute } from "../format";
import { SignedOut } from "./SignedOut";
import { Loading } from "./Loading";
import { ErrorState } from "./ErrorState";
import { EmptyState } from "./EmptyState";

export interface DeviceManagerProps {
  client: ApiClient;
  /** Route to the claim flow — wired into the header + the empty state (#135). */
  onClaim?: () => void;
}

/** Human label + CSS modifier for each lifecycle status. */
const STATUS_LABEL: Record<DeviceStatus, string> = {
  unclaimed: "Unclaimed",
  claimed: "Active",
  revoked: "Revoked",
};

/** The last-sync line: a compact relative time (absolute in the tooltip), or — when the camera has
 *  never synced — an "awaiting first sync" state for a claimed unit, else "Never synced". */
function syncLabel(d: Device): string {
  if (d.last_sync_at != null) return formatRelative(d.last_sync_at);
  return d.status === "claimed" ? "Awaiting first sync" : "Never synced";
}

export function DeviceManager({ client, onClaim }: DeviceManagerProps): React.JSX.Element {
  const devices = useDevices(client);

  if (devices.unauthorized) return <SignedOut />;

  return (
    <section className="devices" data-testid="device-manager">
      <div className="devices__head">
        <h2 className="devices__title">Your cameras</h2>
        <div className="devices__head-actions">
          {onClaim && (
            <button
              type="button"
              className="btn btn--primary"
              onClick={onClaim}
              data-testid="claim-camera"
            >
              Claim a camera
            </button>
          )}
          {!devices.loading && (
            <button
              type="button"
              className="btn"
              onClick={devices.reload}
              disabled={devices.revoking != null}
            >
              Refresh
            </button>
          )}
        </div>
      </div>

      {devices.loading ? (
        <Loading label="Loading your cameras…" />
      ) : devices.error ? (
        <ErrorState
          title="Couldn’t load your cameras"
          message={devices.error}
          onRetry={devices.reload}
        />
      ) : devices.devices.length === 0 ? (
        <EmptyState
          icon="▣"
          title="No cameras yet"
          message="Claim a camera by scanning its QR code (or typing the code) to bind it to your account."
          ctaLabel={onClaim ? "Claim your first camera" : undefined}
          onCta={onClaim}
        />
      ) : (
        <ul className="devices__list" data-testid="device-list">
          {devices.devices.map((d) => (
            <DeviceRow
              key={d.deviceId}
              device={d}
              revoking={devices.revoking === d.deviceId}
              disabled={devices.revoking != null}
              onRevoke={() => devices.revoke(d.deviceId)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

interface DeviceRowProps {
  device: Device;
  revoking: boolean;
  /** True while any row is revoking (one revoke at a time). */
  disabled: boolean;
  onRevoke: () => void | Promise<void>;
}

function DeviceRow({
  device,
  revoking,
  disabled,
  onRevoke,
}: DeviceRowProps): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const isRevoked = device.status === "revoked";
  const name = device.name ?? "Unnamed camera";

  return (
    <li
      className="device"
      data-testid="device-row"
      data-device-id={device.deviceId}
      data-status={device.status}
    >
      <div className="device__main">
        <span className="device__name" title={device.deviceId}>
          {name}
        </span>
        <span className="device__model">{device.model}</span>
      </div>

      <div className="device__meta">
        <span
          className={`device__status device__status--${device.status}`}
          data-testid="device-status"
        >
          {STATUS_LABEL[device.status]}
        </span>
        <span
          className="device__sync"
          title={device.last_sync_at != null ? `Last sync: ${formatAbsolute(device.last_sync_at)}` : undefined}
        >
          {syncLabel(device)}
        </span>
      </div>

      <div className="device__actions">
        {isRevoked ? (
          <span className="device__revoked-note">Access removed</span>
        ) : confirming ? (
          <span
            className="device__confirm"
            onKeyDown={(e) => { if (e.key === "Escape" && !revoking) setConfirming(false); }}
          >
            <span className="device__confirm-q" role="alert">
              Revoke {name}? Its Device token stops working on the next sync — re-Claim to restore.
            </span>
            <button
              type="button"
              className="btn btn--danger"
              onClick={onRevoke}
              disabled={disabled}
              aria-label={`Confirm revoke ${name}`}
            >
              {revoking ? "Revoking…" : "Yes, revoke"}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => setConfirming(false)}
              disabled={revoking}
              autoFocus
            >
              Cancel
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="btn btn--danger"
            onClick={() => setConfirming(true)}
            disabled={disabled}
            aria-label={`Revoke ${name}`}
          >
            Revoke
          </button>
        )}
      </div>
    </li>
  );
}
